import { Alert } from '@designsforhealth/dfh-react-components'
import { Grid, InputAdornment, TextFieldProps, Typography } from '@material-ui/core'
import { Subscribe } from '@react-rxjs/core'
import React, { useContext, useEffect, useReducer } from 'react'

import { CartFragment } from '../../../../graphql/magento'
import useMediaQuery from '../../../../hooks/useMediaQuery'
import {
  logAndCaptureLeft,
  useResultWithLeftEffect,
} from '../../../../hooks/useResultWithLeftEffect'
import useSiteMetadata from '../../../../hooks/useSiteMetadata'
import useUserCategory from '../../../../hooks/useUserCategory'
import { NonEmptyCartContext } from '../../../../lib/cart'
import {
  cartRebateInitialState,
  cartRebateReducer,
} from '../../../../lib/cart/reducers/cart-rebate'
import {
  setApplyRewardsInput,
  useApplyRewards,
  useCustomerAvailableBalance,
} from '../../../../lib/cart/state/rewards'
import { isCartWithAutoShip, isCartWithRebate } from '../../../../lib/cart/utils'
import { useTestKitProducts } from '../../../../lib/products/hooks/useTestKitProducts'
import {
  calculateCashBackOnSpentCommissions,
  commissionsCashBackPercent,
} from '../../../../lib/rewards/commissions'
import { testKitDelayedBilling } from '../../../../utils/buy/paymentTools'
import { constNull } from '../../../../utils/constant'
import { formatAsCurrencyOrZero } from '../../../../utils/priceTools'
import { pluckData } from '../../../../utils/result'
import ErrorUpdatingCartMessage from '../../../ErrorUpdatingCartMessage'
import { MutationErrorAlert } from '../../../MutationErrorAlert'
import { RewardsInformationTooltip } from '../../../shop/account/rewards/RewardsInformationTooltip'
import ApplyRewardsSlider, { RewardsSliderProps } from '../ApplyRewardsSlider'
import { ApplyRewardsSkeleton, getRewardsText } from './Skeleton'
import * as Styled from './styled'

export interface ApplyRewardsProps {
  cart: CartFragment
  className?: string
}

const ApplyRewardsContent: React.FC<ApplyRewardsProps> = ({ cart, className }) => {
  const { featureFlags } = useSiteMetadata() ?? {}
  const { isTestKitSku } = useTestKitProducts()
  const isMobile = useMediaQuery({ maxWidth: '425px' })

  const delayedBilling = testKitDelayedBilling({ cart, isTestKitSku })

  const [, availableBalance] = useResultWithLeftEffect(
    useCustomerAvailableBalance(),
    pluckData,
    constNull,
  )
  const applyRewards = useApplyRewards()

  const [
    { maxAmount, maxApplied, rebateAmount, rebateAmountInput, rebateAmountPending },
    dispatch,
  ] = useReducer(
    cartRebateReducer,
    { availableBalance, cart, delayedBilling },
    cartRebateInitialState,
  )

  const { commissionsBalance, totalBalance } = availableBalance ?? {}

  const { userCategory } = useUserCategory()
  const isPatient = userCategory === 'patient'
  const isPractitioner = userCategory === 'practitioner'

  useEffect(() => {
    const { result } = applyRewards
    if (result._tag === 'Left') {
      logAndCaptureLeft(result)
    }
  }, [applyRewards])

  useEffect(() => {
    setApplyRewardsInput(rebateAmount)
  }, [rebateAmount])

  useEffect(() => {
    dispatch({ type: 'reset', availableBalance, cart, delayedBilling })
  }, [availableBalance, cart, delayedBilling])

  // Early return if the user is a patient and total balance is not greater than 0
  if (isPatient && (!totalBalance || totalBalance.parsed == null || totalBalance.parsed <= 0)) {
    return null
  }

  const cartHasAutoShip = isCartWithAutoShip(cart)
  const showMinimumChargeAlert = isCartWithRebate(cart) && (cartHasAutoShip || delayedBilling)

  const cashBackOnSpentCommissions = calculateCashBackOnSpentCommissions({
    totalBalance: totalBalance?.parsed ?? 0,
    commissionsBalance: commissionsBalance?.parsed ?? 0,
    rebateAmount: Number(rebateAmount),
  })

  const handleSliderChange: RewardsSliderProps['onChange'] = (_e, newValue) => {
    const value = Array.isArray(newValue) ? newValue[0] : newValue
    if (value != null) {
      dispatch({ type: 'change_rebate_slider', value })
    }
  }

  const handleSliderChangeCommitted: RewardsSliderProps['onChangeCommitted'] = (_e, newValue) => {
    const value = Array.isArray(newValue) ? newValue[0] : newValue
    if (value != null) {
      dispatch({ type: 'set_rebate_amount', value })
    }
  }

  const handleApplyMax: RewardsSliderProps['onApplyMax'] = () => {
    dispatch({ type: 'set_rebate_amount', value: maxAmount })
  }

  const handleInputChange: TextFieldProps['onChange'] = (e) => {
    dispatch({ type: 'change_rebate_input', value: e.target.value })
  }

  const handleInputBlur: TextFieldProps['onBlur'] = () => {
    dispatch({ type: 'set_rebate_amount', value: rebateAmountPending })
  }

  const handleInputKeyPress: TextFieldProps['onKeyPress'] = (e) => {
    if (e.key === 'Enter') {
      dispatch({ type: 'set_rebate_amount', value: rebateAmountPending })
    }
  }

  const { rewardsHeader, rewardsLabel } = getRewardsText({ isMobile, isPractitioner })

  return (
    <Styled.Container
      className={className}
      alignItems="center"
      justify="center"
      container
      item
      xs={12}
    >
      <Grid item xs={12}>
        <Styled.Header>
          <Styled.Left>
            <Typography variant="h5">{rewardsHeader}</Typography>
            <RewardsInformationTooltip placement="left">
              We&apos;ll apply store credit, rebates, and commissions, in that order. <br />
              {featureFlags?.commissionsCashBack &&
                '+10% Rewards cash-back applicable to Commissions upon invoice.'}
            </RewardsInformationTooltip>
          </Styled.Left>
          <Styled.Right>{applyRewards.loading && <Styled.LeanSpinner loading />}</Styled.Right>
        </Styled.Header>
        <Styled.TotalBalance>
          <Styled.TotalBalanceText>
            <Styled.RewardsIcon />
            {rewardsLabel}
            <Styled.TotalBalanceAmount>{totalBalance?.amount ?? '0.00'}</Styled.TotalBalanceAmount>
          </Styled.TotalBalanceText>
        </Styled.TotalBalance>
        {totalBalance?.parsed != null && totalBalance.parsed > 0 && (
          <Grid item xs={12} container direction="column">
            <Grid container spacing={2}>
              <Grid item xs>
                <ApplyRewardsSlider
                  disabled={false}
                  maxAmount={maxAmount}
                  maxApplied={maxApplied}
                  onApplyMax={handleApplyMax}
                  onChange={handleSliderChange}
                  onChangeCommitted={handleSliderChangeCommitted}
                  value={rebateAmountPending}
                />
              </Grid>
              <Grid item>
                <Styled.RewardsContainer>
                  <Styled.RewardsField
                    inputProps={{ inputMode: 'numeric' }}
                    InputProps={{
                      disableUnderline: true,
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      type: 'text',
                    }}
                    label={`Rewards (max ${formatAsCurrencyOrZero({ amount: maxAmount })})`}
                    name="rebateAmount"
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    onKeyPress={handleInputKeyPress}
                    value={rebateAmountInput}
                    variant="filled"
                  />
                </Styled.RewardsContainer>
              </Grid>
            </Grid>
            {featureFlags?.commissionsCashBack && (
              <Styled.CashbackLabel $cashBackOnSpentCommissions={cashBackOnSpentCommissions}>
                {commissionsCashBackPercent}% cash-back on spent commissions{' '}
                <Styled.CashbackAmount>
                  {formatAsCurrencyOrZero({ amount: cashBackOnSpentCommissions })}
                </Styled.CashbackAmount>
              </Styled.CashbackLabel>
            )}
            {showMinimumChargeAlert && (
              <Styled.MinimumChargeAlertContainer>
                {cartHasAutoShip && (
                  <Alert type="attention">
                    Please note as you have added Auto-Ship item/s to your cart, a mandatory $0.01
                    will be charged at this time to validate your Credit Card for future orders.{' '}
                    <br />
                    <br />
                    Your Auto-Ship subscription will thereafter automatically create a new order
                    according to your chosen delivery schedule, and charge the full amount on this
                    validated credit card, until you cancel.
                  </Alert>
                )}
                {delayedBilling && (
                  <Alert type="attention">
                    The Spotlight test kit/s in your cart will require an on file payment method to
                    be charged once it is processed by DSL. A mandatory $0.01 will be charged at
                    this time to validate your payment method for future use once the test kit is
                    processed.
                  </Alert>
                )}
              </Styled.MinimumChargeAlertContainer>
            )}
          </Grid>
        )}
        {applyRewards.result._tag === 'Left' && (
          <MutationErrorAlert
            onCancel={() => dispatch({ type: 'reset', availableBalance, cart, delayedBilling })}
            onRetry={() => setApplyRewardsInput(rebateAmount)}
          >
            <ErrorUpdatingCartMessage withoutContactSupport />
          </MutationErrorAlert>
        )}
      </Grid>
    </Styled.Container>
  )
}

export const ApplyRewards: React.FC = () => {
  const customerCart = useContext(NonEmptyCartContext)

  return (
    <Subscribe fallback={<ApplyRewardsSkeleton />}>
      <ApplyRewardsContent cart={customerCart} />
    </Subscribe>
  )
}
